import {useEffect, useRef, useState} from 'react';
import {Box, Button, LinearProgress, Typography} from '@mui/material';

const PULL_TO_GO = 30;          // wheel distance past the edge: a light nudge, or one notch of a mouse wheel
const NEW_GESTURE_GAP_MS = 200; // a wheel quiet for this long has stopped; the next push is a new one
const COOLDOWN_MS = 700;        // after moving on, the next form ignores the wheel still turning
const IDLE_RESET_MS = 800;

// Shared by every cue: a new form appearing under a wheel that is still turning must not chain on to the next one.
let lastWheelAt = 0;
let lastWheelSpeed = 0;
let lastGoAt = 0;

/**
 * The start or end of a work item's form: a button to the neighbouring item,
 * and the same thing by scrolling past that edge.
 *
 * A push counts when it starts at the edge after the wheel has been quiet, or
 * when it is a new swipe on top of the last one's momentum (the speed jumps
 * back up). The slowing tail of a fling that carried you to the edge never
 * counts, so skimming a form does not skip it. onGo asks about unsaved changes first.
 */
export function ScrollPastCue({edge, label, onGo}: {edge: 'start' | 'end'; label: string; onGo: () => void}) {
  const cue = useRef<HTMLDivElement>(null);
  const go = useRef(onGo);
  go.current = onGo;
  const [pull, setPull] = useState(0);

  useEffect(() => {
    const region = cue.current?.closest<HTMLElement>('.MuiCardContent-root');
    if (!region) return;
    const direction = edge === 'end' ? 1 : -1;
    let pulled = 0, armed = false, idle: number | undefined;
    const atEdge = () => edge === 'end'
      ? region.scrollTop + region.clientHeight >= region.scrollHeight - 2
      : region.scrollTop <= 1;
    const reset = () => { pulled = 0; armed = false; setPull(0); };
    const onWheel = (event: WheelEvent) => {
      // Where the page scrolls instead (phones), only the button applies.
      if (getComputedStyle(region).overflowY !== 'auto') return;
      const now = performance.now();
      const speed = Math.abs(event.deltaY);
      const isNewPush = now - lastWheelAt > NEW_GESTURE_GAP_MS || speed > lastWheelSpeed * 1.5 + 3;
      lastWheelAt = now;
      lastWheelSpeed = speed;
      window.clearTimeout(idle);
      if (Math.sign(event.deltaY) !== direction || !atEdge()) { reset(); return; }
      if (!armed && isNewPush && now - lastGoAt > COOLDOWN_MS) armed = true;
      if (!armed) return;
      pulled += speed;
      if (pulled >= PULL_TO_GO) { lastGoAt = now; reset(); go.current(); return; }
      setPull(pulled / PULL_TO_GO);
      idle = window.setTimeout(reset, IDLE_RESET_MS);
    };
    region.addEventListener('wheel', onWheel, {passive: true});
    return () => { region.removeEventListener('wheel', onWheel); window.clearTimeout(idle); };
  }, [edge]);

  const progress = <LinearProgress variant="determinate" value={pull * 100} aria-hidden sx={{mt: 0.5, opacity: pull > 0 ? 1 : 0}}/>;
  return edge === 'end' ? (
    <Box ref={cue} data-testid="next-item-cue" sx={{mt: 2, p: 2, border: 1, borderStyle: 'dashed', borderColor: 'divider', borderRadius: 1, textAlign: 'center'}}>
      <Typography variant="body2" color="text.secondary">Done with this one? Keep scrolling, or</Typography>
      <Button sx={{mt: 0.5}} onClick={onGo}>Next: {label} →</Button>
      {progress}
    </Box>
  ) : (
    <Box ref={cue} data-testid="previous-item-cue" sx={{mb: 1}}>
      <Button size="small" onClick={onGo}>← Previous: {label}</Button>
      <Typography component="span" variant="caption" color="text.secondary" sx={{ml: 1}}>or keep scrolling up</Typography>
      {progress}
    </Box>
  );
}
